import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Select,
  Switch,
  Skeleton,
  Tag,
  DatePicker,
  Popover,
  Tabs,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  ThunderboltOutlined,
  ApiOutlined,
  RobotOutlined,
  ReloadOutlined,
  WarningOutlined,
  SearchOutlined,
  ClearOutlined,
  PlayCircleOutlined,
  AreaChartOutlined,
  MessageOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  DownOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type { ActivityEvent } from '@/api/activity';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { ActivityDrawer } from './ActivityDrawer';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';

const { Text } = Typography;

// Consolidated runtime feed: every LLM call, tool invocation, and A2A peer
// hop in one timeline. Each row is click-throughable into a drawer that
// pulls every other event sharing the same correlation id (or thread id) —
// "open this event's full trace" answers most debug questions in one click.

const ISSUE_TONE: Record<string, PillTone> = {
  errored: 'red',
  timeout: 'red',
  'context-critical': 'red',
  'repeated-args-hash': 'red',
  'context-warning': 'amber',
  slow: 'amber',
  'high-cost': 'amber',
  'oversized-args': 'amber',
  'oversized-response': 'amber',
  'data-dump-tool': 'amber',
  'enumeration-output': 'amber',
  'zero-retrieval-result': 'amber',
  'low-retrieval-score': 'amber',
  'deep-chain': 'violet',
};

const ISSUE_LABEL: Record<string, string> = {
  errored: 'Errored',
  timeout: 'Timed out',
  'context-critical': 'Context critical',
  'context-warning': 'Context warning',
  slow: 'Slow',
  'high-cost': 'High cost',
  'oversized-args': 'Oversized args',
  'oversized-response': 'Oversized response',
  'data-dump-tool': 'Data-dump tool',
  'enumeration-output': 'Enumeration output',
  'repeated-args-hash': 'Tool loop',
  'zero-retrieval-result': 'Zero KB results',
  'low-retrieval-score': 'Low KB score',
  'deep-chain': 'Deep A2A chain',
};

const KIND_ICON: Record<string, React.ReactNode> = {
  llm: <ThunderboltOutlined />,
  tool: <ApiOutlined />,
  consult: <RobotOutlined />,
  retrieval: <ReadOutlined />,
};

const KIND_TONE: Record<string, PillTone> = {
  llm: 'ink',
  tool: 'amber',
  consult: 'violet',
  retrieval: 'green',
};

const KIND_LABEL: Record<string, string> = {
  llm: 'LLM',
  tool: 'Tool',
  consult: 'A2A',
  retrieval: 'KB',
};

export const ActivityPage = observer(function ActivityPage() {
  const {
    activityStore: store,
    merchantContextStore: ctx,
    themeStore,
  } = useStore();
  const isDark = themeStore.mode === 'dark';

  useEffect(() => {
    if (ctx.selectedMerchantId != null && store.events.length === 0 && !store.isLoading) {
      void store.fetch();
      void store.fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<AreaChartOutlined />}
          title="Pick a merchant to begin"
          description="Activity is scoped to a single merchant via PromptLog/ToolCallingLog/ConsultationLog merchant ids."
          size="lg"
        />
      </SectionCard>
    );
  }

  const summary = store.summary;
  const filterCount =
    (store.agentFilter ? 1 : 0) +
    (store.kindFilter !== 'all' ? 1 : 0) +
    (store.onlyIssues ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<ThunderboltOutlined />}
        title={
          <Space size={6} align="center">
            <span>Activity</span>
            <HelpHotspot topic="validations.overview" />
          </Space>
        }
        subtitle="One timeline · LLM calls · tool invocations · A2A consultations · with server-tagged issues."
        actions={
          <Space>
            <TimeRangePicker />
            <AgentPickerTrigger
              value={store.agentFilter}
              onChange={(id) => store.setAgentFilter(id)}
              placeholder="Any agent"
              clearLabel="Any agent — show all"
            />
            <Select
              style={{ width: 130 }}
              value={store.kindFilter}
              onChange={(v) => store.setKindFilter(v)}
              options={[
                { value: 'all', label: 'All kinds' },
                { value: 'llm', label: 'LLM only' },
                { value: 'tool', label: 'Tool only' },
                { value: 'consult', label: 'A2A only' },
                { value: 'retrieval', label: 'KB only' },
              ]}
            />
            <Tooltip title="Show only events flagged with one or more issues">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  size="small"
                  checked={store.onlyIssues}
                  onChange={(v) => store.setOnlyIssues(v)}
                />
                <WarningOutlined style={{ color: colors.amber[600] }} /> Issues
              </span>
            </Tooltip>
            <Tooltip title="Auto-refresh every 10 s">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  size="small"
                  checked={store.autoRefresh}
                  onChange={(v) => store.setAutoRefresh(v)}
                />
                Live
              </span>
            </Tooltip>
            {filterCount > 0 && (
              <Button
                type="text"
                size="small"
                icon={<ClearOutlined />}
                onClick={() => {
                  store.setAgentFilter(null);
                  store.setKindFilter('all');
                  store.setOnlyIssues(false);
                }}
              >
                Clear
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={store.isLoading} onClick={() => void store.fetch()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {/* Stat strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <Stat label="Events" value={summary.total} accent="ink" isDark={isDark} />
        <Stat label="LLM" value={summary.llm} accent="ink" isDark={isDark} />
        <Stat label="Tool" value={summary.tool} accent="amber" isDark={isDark} />
        <Stat label="A2A" value={summary.consult} accent="violet" isDark={isDark} />
        <Stat label="KB" value={summary.retrieval} accent="green" isDark={isDark} />
        <Stat
          label="With issues"
          value={summary.issues}
          accent={summary.issues > 0 ? 'amber' : 'ink'}
          isDark={isDark}
        />
        <Stat
          label="Errored"
          value={summary.errored}
          accent={summary.errored > 0 ? 'red' : 'ink'}
          isDark={isDark}
        />
      </div>

      {store.appInsightsConfigured === false && (
        <Alert
          type="info"
          showIcon
          closable
          message="Want exception stacks + dependency latency too?"
          description={
            <span style={{ fontSize: 12.5 }}>
              Set <code>ApplicationInsights:QueryApiKey</code> in <code>appsettings</code> and the
              page will surface unhandled exceptions and Azure OpenAI dependency timing alongside
              the SQL-backed timeline. Generate a key under App Insights → Configure → API Access.
            </span>
          }
        />
      )}

      {store.error && (
        <Alert type="error" showIcon message="Failed to load activity" description={store.error} closable />
      )}

      {/* Search applies to all three views */}
      <SectionCard noPadding>
        <div style={{ padding: '10px 16px' }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
            placeholder="Filter by agent / model / plugin / thread / correlation id…"
            value={store.search}
            onChange={(e) => store.setSearch(e.target.value)}
          />
        </div>
      </SectionCard>

      {/* Three views — same data, different lenses */}
      <Tabs
        defaultActiveKey="timeline"
        tabBarStyle={{ marginBottom: 0, paddingLeft: 4 }}
        items={[
          {
            key: 'timeline',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ThunderboltOutlined /> Timeline
                <span style={{ fontSize: 11, color: colors.slate[400], fontFamily: "'JetBrains Mono', monospace" }}>
                  {store.filtered.length}
                </span>
              </span>
            ),
            children: <TimelineView isDark={isDark} />,
          },
          {
            key: 'conversations',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <MessageOutlined /> Conversations
                <span style={{ fontSize: 11, color: colors.slate[400], fontFamily: "'JetBrains Mono', monospace" }}>
                  {countConversations(store.filtered)}
                </span>
              </span>
            ),
            children: <ConversationsView isDark={isDark} />,
          },
          {
            key: 'issues',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ExclamationCircleOutlined
                  style={{ color: summary.issues > 0 ? colors.amber[600] : undefined }}
                />
                Issues
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: summary.issues > 0 ? colors.amber[600] : colors.slate[400],
                    fontWeight: summary.issues > 0 ? 600 : 400,
                  }}
                >
                  {summary.issues}
                </span>
              </span>
            ),
            children: <IssuesView isDark={isDark} />,
          },
        ]}
      />

      <ActivityDrawer />
    </div>
  );
});

// ── Conversation grouping ───────────────────────────────────────────────────
//
// A "conversation" is the set of events that share a ThreadId. We also gather
// orphan events (no thread) under a single bucket so they stay visible.

interface ConversationGroup {
  threadId: string | null;     // null = orphan bucket
  events: ActivityEvent[];
  agentNames: string[];
  startedAt: Date;
  endedAt: Date;
  llmCount: number;
  toolCount: number;
  consultCount: number;
  totalCost: number;
  hasIssues: boolean;
  errored: boolean;
}

function groupByConversation(events: ActivityEvent[]): ConversationGroup[] {
  const buckets = new Map<string, ActivityEvent[]>();
  const ORPHAN = '__orphan__';
  for (const e of events) {
    const key = e.threadId && e.threadId.length > 0 ? e.threadId : ORPHAN;
    const arr = buckets.get(key) ?? [];
    arr.push(e);
    buckets.set(key, arr);
  }
  const groups: ConversationGroup[] = [];
  for (const [key, arr] of buckets) {
    const sorted = [...arr].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const agentNames = Array.from(new Set(sorted.map((e) => e.agentName).filter(Boolean) as string[]));
    groups.push({
      threadId: key === ORPHAN ? null : key,
      events: sorted,
      agentNames,
      startedAt: new Date(sorted[0].timestamp),
      endedAt: new Date(sorted[sorted.length - 1].timestamp),
      llmCount: sorted.filter((e) => e.kind === 'llm').length,
      toolCount: sorted.filter((e) => e.kind === 'tool').length,
      consultCount: sorted.filter((e) => e.kind === 'consult').length,
      totalCost: sorted.reduce((acc, e) => acc + (e.estimatedCostUsd ?? 0), 0),
      hasIssues: sorted.some((e) => e.issueTags && e.issueTags.length > 0),
      errored: sorted.some((e) => e.errored),
    });
  }
  // Most recent conversation first.
  groups.sort((a, b) => b.endedAt.getTime() - a.endedAt.getTime());
  return groups;
}

function countConversations(events: ActivityEvent[]): number {
  return new Set(events.map((e) => e.threadId).filter(Boolean)).size;
}

// ── Tab views ───────────────────────────────────────────────────────────────

const TimelineView = observer(function TimelineView({ isDark }: { isDark: boolean }) {
  const { activityStore: store } = useStore();
  if (store.isLoading && store.events.length === 0) {
    return (
      <SectionCard>
        <Skeleton active paragraph={{ rows: 6 }} />
      </SectionCard>
    );
  }
  if (store.filtered.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ThunderboltOutlined />}
          title={store.events.length === 0 ? 'Nothing in window' : 'Nothing matches the filters'}
          description={
            store.events.length === 0
              ? 'Trigger an agent conversation — rows will land here within seconds.'
              : 'Try clearing filters or widening the time window.'
          }
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard noPadding>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {store.filtered.map((e) => (
          <ActivityRow key={`${e.kind}-${e.id}`} event={e} isDark={isDark} />
        ))}
      </div>
    </SectionCard>
  );
});

const ConversationsView = observer(function ConversationsView({ isDark }: { isDark: boolean }) {
  const { activityStore: store } = useStore();
  const groups = groupByConversation(store.filtered);

  if (store.isLoading && store.events.length === 0) {
    return <SectionCard><Skeleton active paragraph={{ rows: 6 }} /></SectionCard>;
  }
  if (groups.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<MessageOutlined />}
          title="No conversations in window"
          description="Conversations group events by ThreadId. Trigger an agent chat — rows will fold into conversation cards here."
        />
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map((g, i) => (
        <ConversationCard key={g.threadId ?? `orphan-${i}`} group={g} isDark={isDark} />
      ))}
    </div>
  );
});

const ConversationCard = observer(function ConversationCard({
  group,
  isDark,
}: {
  group: ConversationGroup;
  isDark: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { activityStore: store } = useStore();
  const cardBg = isDark ? colors.slate[800] : '#FFFFFF';
  const cardBorder = group.errored
    ? isDark ? colors.red[700] + 'aa' : colors.red[100]
    : group.hasIssues
      ? isDark ? colors.amber[700] + 'aa' : colors.amber[100]
      : isDark ? colors.slate[700] : colors.slate[200];
  const subColor = isDark ? colors.slate[400] : colors.slate[500];

  const durationMs = group.endedAt.getTime() - group.startedAt.getTime();

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          cursor: 'pointer',
        }}
      >
        <span style={{ color: subColor, fontSize: 11 }}>
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: isDark ? colors.slate[50] : colors.slate[900] }}>
              {group.threadId ? group.agentNames.join(' + ') || 'Conversation' : 'Orphaned events'}
            </span>
            {group.threadId && (
              <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: subColor }}>
                {group.threadId.length > 16 ? group.threadId.slice(0, 8) + '…' + group.threadId.slice(-6) : group.threadId}
              </span>
            )}
            {group.errored && <StatusPill tone="red">errored</StatusPill>}
            {!group.errored && group.hasIssues && <StatusPill tone="amber">issues</StatusPill>}
          </div>
          <div style={{ fontSize: 12, color: subColor, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <span>
              {group.events.length} event{group.events.length === 1 ? '' : 's'}
              {group.llmCount > 0 && <> · {group.llmCount} LLM</>}
              {group.toolCount > 0 && <> · {group.toolCount} tool</>}
              {group.consultCount > 0 && <> · {group.consultCount} A2A</>}
            </span>
            <span>{formatMs(durationMs)} span</span>
            {group.totalCost > 0 && <span>${group.totalCost.toFixed(4)}</span>}
            <span>{group.startedAt.toLocaleString()}</span>
          </div>
        </div>
        {group.threadId && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (group.threadId) void store.openThread(group.threadId);
            }}
          >
            Open trace
          </Button>
        )}
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}` }}>
          {group.events.map((e) => (
            <ActivityRow key={`${e.kind}-${e.id}`} event={e} isDark={isDark} />
          ))}
        </div>
      )}
    </div>
  );
});

const ISSUE_PRIORITY: Record<string, number> = {
  errored: 0,
  timeout: 1,
  'repeated-args-hash': 2,         // tool loop — almost always a real bug
  'context-critical': 2,
  oversized: 3,
  'oversized-args': 3,
  'oversized-response': 3,
  'data-dump-tool': 3,
  'context-warning': 4,
  'enumeration-output': 4,
  'zero-retrieval-result': 4,
  'low-retrieval-score': 5,
  'high-cost': 5,
  'deep-consult': 6,
  'deep-chain': 6,
  slow: 7,
  // 'compacted' intentionally omitted — compaction is a successful
  // protective action, not an issue. Surfaced separately as info.
};

const ISSUE_NEXT_ACTION: Record<string, string> = {
  errored: 'Open trace to see exception detail',
  timeout: 'Likely cold-start or model overload — open trace',
  'context-critical': 'Prompt is approaching the model context window — review compaction',
  oversized: 'Tool argument or response is unusually large — could be a tool loop',
  'oversized-args': 'Tool argument is unusually large — check the caller',
  'oversized-response': 'Tool response is unusually large — consider returning a summary or AGUI inline',
  'data-dump-tool': 'Tool response is large AND fully visible to the LLM — consider returning a summary or fusing with a chart tool',
  'context-warning': 'Watch this — context will overflow soon',
  'enumeration-output': 'Model wrote a long bullet list — usually means a data-dump tool fed it raw rows. Check the preceding tool call.',
  'high-cost': 'Single turn cost is unusually high — review prompt size and tool calls',
  'deep-consult': 'A2A consultation chain is going deep — potential cycle',
  'deep-chain': 'A2A chain depth at or beyond threshold — check for cycles',
  slow: 'Latency above threshold (model-aware: reasoning models get a 3× allowance) — worth investigating if recurring',
  'repeated-args-hash': 'Same tool called 3+ times with identical arguments in this request — almost always a context-feedback loop; check whether the LLM is absorbing the response',
  'zero-retrieval-result': 'Knowledge base returned 0 chunks — query may be off-topic for the linked KBs OR the KBs need refresh / better content',
  'low-retrieval-score': 'Top-K best match was below the relevance threshold — the agent likely answered without strong grounding; review the query and KB coverage',
};

const IssuesView = observer(function IssuesView({ isDark }: { isDark: boolean }) {
  const { activityStore: store } = useStore();

  const issues = store.filtered
    .filter((e) => (e.issueTags && e.issueTags.length > 0) || e.errored)
    .sort((a, b) => {
      const ap = Math.min(...(a.issueTags ?? ['']).map((t) => ISSUE_PRIORITY[t] ?? 99));
      const bp = Math.min(...(b.issueTags ?? ['']).map((t) => ISSUE_PRIORITY[t] ?? 99));
      if (ap !== bp) return ap - bp;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

  if (store.isLoading && store.events.length === 0) {
    return <SectionCard><Skeleton active paragraph={{ rows: 6 }} /></SectionCard>;
  }
  if (issues.length === 0) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ExclamationCircleOutlined />}
          title="No issues in window"
          description="Server-tagged issues — slow / oversized / context overflow / errored / deep-consult / high-cost — surface here ranked by severity."
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard noPadding>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {issues.map((e) => (
          <IssueRow key={`${e.kind}-${e.id}`} event={e} isDark={isDark} />
        ))}
      </div>
    </SectionCard>
  );
});

const IssueRow = observer(function IssueRow({
  event,
  isDark,
}: {
  event: ActivityEvent;
  isDark: boolean;
}) {
  const { activityStore: store } = useStore();
  const [hover, setHover] = useState(false);
  const tags = event.issueTags ?? [];
  // Pick the highest-priority tag for the suggested action; errored takes
  // precedence over any other tag because it usually IS the root cause.
  const primary = event.errored ? 'errored' : tags.slice().sort((a, b) => (ISSUE_PRIORITY[a] ?? 99) - (ISSUE_PRIORITY[b] ?? 99))[0];
  const action = ISSUE_NEXT_ACTION[primary ?? ''] ?? 'Open trace for full detail';
  const subColor = isDark ? colors.slate[400] : colors.slate[500];

  // Default click target: trace drawer when correlationId is available,
  // otherwise the thread drawer. The whole row is the click surface so the
  // engineer can hit anywhere — the suggested-action text effectively
  // becomes a hyperlink.
  const canDrillIn = !!event.correlationId || !!event.threadId;
  const drillIn = () => {
    if (event.correlationId) void store.openTrace(event.correlationId);
    else if (event.threadId) void store.openThread(event.threadId);
  };

  const hoverBg = isDark ? colors.slate[800] : colors.slate[50];
  const baseBg = 'transparent';

  return (
    <div
      onClick={canDrillIn ? drillIn : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
        cursor: canDrillIn ? 'pointer' : 'default',
        background: canDrillIn && hover ? hoverBg : baseBg,
        transition: 'background 80ms ease',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          {event.errored && <StatusPill tone="red">errored</StatusPill>}
          {tags.map((t) => (
            <StatusPill key={t} tone={ISSUE_TONE[t] ?? 'amber'}>
              {ISSUE_LABEL[t] ?? t}
            </StatusPill>
          ))}
          <StatusPill tone={KIND_TONE[event.kind] ?? 'slate'}>{KIND_LABEL[event.kind] ?? event.kind}</StatusPill>
        </div>
        <div style={{ fontSize: 13, color: isDark ? colors.slate[100] : colors.slate[800], lineHeight: 1.45 }}>
          {event.errorMessage
            || (event.kind === 'tool' ? `${event.pluginName}.${event.methodName}` : null)
            || (event.kind === 'llm' ? `${event.modelName ?? 'LLM call'} on ${event.agentName ?? 'unknown agent'}` : null)
            || (event.kind === 'consult' ? `${event.primaryAgentName ?? '?'} → ${event.peerAgentName ?? '?'}` : null)
            || 'Issue'}
        </div>
        <div
          style={{
            fontSize: 12,
            color: canDrillIn ? colors.ink[isDark ? 200 : 600] : subColor,
            marginTop: 4,
            fontWeight: canDrillIn ? 500 : 400,
            textDecoration: canDrillIn && hover ? 'underline' : 'none',
          }}
        >
          {action}
          {canDrillIn && (
            <span style={{ marginLeft: 6, fontSize: 11, opacity: hover ? 1 : 0.7 }}>→</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: subColor, marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
          {event.agentName ?? '—'} · {formatMs(event.durationMs)} · {new Date(event.timestamp).toLocaleString()}
        </div>
      </div>
      <Space direction="vertical" size={4} onClick={(e) => e.stopPropagation()}>
        {event.correlationId && (
          <Button size="small" onClick={() => store.openTrace(event.correlationId!)}>
            Open trace
          </Button>
        )}
        {event.threadId && (
          <Button size="small" onClick={() => store.openThread(event.threadId!)}>
            Thread
          </Button>
        )}
      </Space>
    </div>
  );
});

// ── Single event row ───────────────────────────────────────────────────────

const ActivityRow = observer(function ActivityRow({
  event,
  isDark,
}: {
  event: ActivityEvent;
  isDark: boolean;
}) {
  const { activityStore: store } = useStore();
  const tone = KIND_TONE[event.kind] ?? 'slate';
  const fg =
    tone === 'amber' ? colors.amber[600]
      : tone === 'violet' ? colors.violet[600]
      : colors.ink[600];

  const ts = new Date(event.timestamp);
  const time = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = ts.toLocaleDateString();

  // Build the meaty middle column based on kind
  const detail =
    event.kind === 'llm' ? (
      <LlmDetail event={event} isDark={isDark} />
    ) : event.kind === 'tool' ? (
      <ToolDetail event={event} isDark={isDark} />
    ) : event.kind === 'retrieval' ? (
      <RetrievalDetail event={event} isDark={isDark} />
    ) : (
      <ConsultDetail event={event} isDark={isDark} />
    );

  return (
    <div
      onClick={() => {
        if (event.correlationId) {
          trackEvent('Activity.TraceOpened', { correlationId: event.correlationId });
          void store.openTrace(event.correlationId);
        } else if (event.threadId) {
          void store.openThread(event.threadId);
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 90px 1fr 220px 130px',
        gap: 12,
        padding: '10px 16px',
        borderTop: `1px solid ${isDark ? colors.slate[800] : colors.slate[100]}`,
        cursor: event.correlationId || event.threadId ? 'pointer' : 'default',
        background: 'transparent',
        transition: 'background 100ms ease',
        alignItems: 'center',
      }}
      onMouseEnter={(el) => {
        el.currentTarget.style.background = isDark ? colors.slate[800] : colors.slate[50];
      }}
      onMouseLeave={(el) => {
        el.currentTarget.style.background = 'transparent';
      }}
    >
      {/* Timestamp */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: isDark ? colors.slate[100] : colors.slate[900],
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {time}
        </Text>
        <Text style={{ fontSize: 10.5, color: isDark ? colors.slate[500] : colors.slate[400] }}>
          {date}
        </Text>
      </div>

      {/* Kind badge */}
      <div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 999,
            background: `${fg}1A`,
            color: fg,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {KIND_ICON[event.kind]}
          {KIND_LABEL[event.kind]}
        </span>
      </div>

      {/* Detail */}
      <div style={{ minWidth: 0 }}>{detail}</div>

      {/* Issue tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {event.issueTags.length === 0 ? (
          <Text style={{ fontSize: 11, color: isDark ? colors.slate[500] : colors.slate[400] }}>
            —
          </Text>
        ) : (
          event.issueTags.map((tag) => (
            <StatusPill key={tag} tone={ISSUE_TONE[tag] ?? 'slate'}>
              {ISSUE_LABEL[tag] ?? tag}
            </StatusPill>
          ))
        )}
      </div>

      {/* Duration */}
      <div style={{ textAlign: 'right' }}>
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: isDark ? colors.slate[200] : colors.slate[700],
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatMs(event.durationMs)}
        </Text>
      </div>
    </div>
  );
});

// ── Per-kind detail blocks ─────────────────────────────────────────────────

function LlmDetail({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.agentName ?? `Agent #${event.agentId ?? '—'}`}{' '}
        <span
          style={{
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[400] : colors.slate[500],
            marginLeft: 6,
          }}
        >
          {event.modelName ?? '—'}
        </span>
      </div>
      <div
        style={{
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginTop: 2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {(event.inputTokens ?? 0).toLocaleString()} in →{' '}
        {(event.outputTokens ?? 0).toLocaleString()} out
        {event.cachedTokens ? ` · ${event.cachedTokens.toLocaleString()} cached` : ''}
        {event.estimatedCostUsd != null
          ? ` · $${Number(event.estimatedCostUsd).toFixed(4)}`
          : ''}
        {event.percentOfContext != null
          ? ` · ${Number(event.percentOfContext).toFixed(1)}% ctx`
          : ''}
      </div>
    </div>
  );
}

function ToolDetail({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
          fontFamily: "'JetBrains Mono', monospace",
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.pluginName ?? '?'}.{event.methodName ?? '?'}
      </div>
      <div
        style={{
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginTop: 2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {(event.argumentChars ?? 0).toLocaleString()} ch args ·{' '}
        {(event.responseChars ?? 0).toLocaleString()} ch resp
        {event.agentName ? ` · ${event.agentName}` : ''}
      </div>
    </div>
  );
}

function ConsultDetail({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.primaryAgentName ?? `Agent #${event.agentId}`}{' '}
        <span style={{ color: isDark ? colors.slate[400] : colors.slate[500] }}>→</span>{' '}
        {event.peerAgentName ?? `Agent #${event.peerAgentId}`}
      </div>
      <div
        style={{
          fontSize: 11,
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontStyle: 'italic',
        }}
      >
        depth {event.consultationDepth ?? 0}
        {event.question ? ` · "${event.question.slice(0, 70)}"` : ''}
      </div>
    </div>
  );
}

function RetrievalDetail({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  const subColor = isDark ? colors.slate[400] : colors.slate[500];
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        KB query
        {event.indexName ? ` · ${event.indexName}` : ''}
      </div>
      {event.retrievalQuery && (
        <div
          style={{
            fontSize: 12,
            color: isDark ? colors.slate[200] : colors.slate[700],
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontStyle: 'italic',
          }}
          title={event.retrievalQuery}
        >
          “{event.retrievalQuery}”
        </div>
      )}
      <div style={{ fontSize: 11, color: subColor, marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
        {event.resultCount ?? 0} result{event.resultCount === 1 ? '' : 's'}
        {event.topScore != null && ` · top ${Number(event.topScore).toFixed(2)}`}
        {event.zeroResults && ' · ⚠ zero results'}
      </div>
    </div>
  );
}

// ── Stat tile ───────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  accent,
  isDark,
}: {
  label: string;
  value: number;
  accent: PillTone;
  isDark: boolean;
}) {
  const fg =
    accent === 'red' ? colors.red[600]
      : accent === 'amber' ? colors.amber[600]
      : accent === 'green' ? colors.green[600]
      : accent === 'violet' ? colors.violet[600]
      : colors.ink[600];
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: fg,
          fontFamily: "'JetBrains Mono', monospace",
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function formatMs(ms: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${(sec / 60).toFixed(1)}m`;
}

// Dummy reference to silence unused import warnings in some build setups
void Tag;
void PlayCircleOutlined;

// App-Insights-style range control: a labeled trigger that opens a popover
// with preset chips (30 m / 1 h / 4 h / 24 h / 48 h / 7 d / 30 d / 90 d) and
// a custom start-end picker. Clicking a preset closes the popover and applies
// immediately; the custom path requires explicit Apply so users don't fire a
// query mid-edit.
const PRESETS: { label: string; hours: number }[] = [
  { label: 'Last 30 m', hours: 0.5 },
  { label: 'Last 1 h', hours: 1 },
  { label: 'Last 4 h', hours: 4 },
  { label: 'Last 24 h', hours: 24 },
  { label: 'Last 48 h', hours: 48 },
  { label: 'Last 7 d', hours: 24 * 7 },
  { label: 'Last 30 d', hours: 24 * 30 },
  { label: 'Last 90 d', hours: 24 * 90 },
];

function presetLabel(hours: number): string {
  return PRESETS.find((p) => p.hours === hours)?.label ?? `Last ${hours} h`;
}

const TimeRangePicker = observer(function TimeRangePicker() {
  const { activityStore: store } = useStore();
  const triggerLabel = store.isCustomRange
    ? `${dayjs(store.customFrom!).format('MMM D HH:mm')} → ${dayjs(store.customTo!).format('MMM D HH:mm')}`
    : presetLabel(store.presetHours);

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      content={<TimeRangePickerContent />}
    >
      <Button>{triggerLabel}</Button>
    </Popover>
  );
});

const TimeRangePickerContent = observer(function TimeRangePickerContent() {
  const { activityStore: store } = useStore();
  const [draftRange, setDraftRange] = useState<[Dayjs, Dayjs] | null>(
    store.customFrom && store.customTo ? [dayjs(store.customFrom), dayjs(store.customTo)] : null
  );

  return (
    <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            size="small"
            type={!store.isCustomRange && store.presetHours === p.hours ? 'primary' : 'default'}
            onClick={() => store.setPreset(p.hours)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
          Custom range (UTC)
        </Text>
        <DatePicker.RangePicker
          showTime={{ format: 'HH:mm' }}
          format="MMM D · HH:mm"
          value={draftRange}
          onChange={(v) => setDraftRange(v as [Dayjs, Dayjs] | null)}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          {store.isCustomRange && (
            <Button size="small" onClick={() => store.clearCustomRange()}>Clear</Button>
          )}
          <Button
            type="primary"
            size="small"
            disabled={!draftRange || !draftRange[0] || !draftRange[1]}
            onClick={() => {
              if (draftRange && draftRange[0] && draftRange[1]) {
                store.setCustomRange(draftRange[0].toISOString(), draftRange[1].toISOString());
              }
            }}
          >
            Apply custom
          </Button>
        </div>
      </div>
    </div>
  );
});
